import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { resolveAssetUrl } from '@/utils/urls';
import AircraftImage from '@/features/tails/components/AircraftImage';
import { probeImageUrl } from '@/features/tails/utils';
import { getAircraftBundledCarouselUrls } from '@assets/images';

// Same tile source the Track page uses (FlightTracker.jsx:16). Swapped
// from CARTO Positron to Esri World Light Gray Canvas — CARTO now
// requires an API key for its public basemap endpoint (watermark
// appears otherwise), while Esri's tile service is still open with
// CORS enabled. Visually similar (light gray canvas), no key needed.
const CARTO_TILES = 'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}';
const CARTO_ATTRIBUTION = 'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ';

// divIcon side-steps Leaflet's default marker icons (which load PNGs from a
// bundle path that breaks under most bundlers). Same approach as Track's
// FlightTracker.jsx — pure inline HTML, no external asset dependency.
const airportIcon = L.divIcon({
  html:
    '<div style="width:12px;height:12px;border-radius:50%;background:#0C3E71;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.3)"></div>',
  className: '',
  iconSize: [12, 12],
  iconAnchor: [6, 6],
});

// Accept lat/lng, latitude/longitude, or nested airport objects — the
// backend and the leg-hydration flows use slightly different shapes.
const extractCoord = (obj) => {
  if (!obj || typeof obj !== 'object') return null;
  const lat = obj.latitude ?? obj.lat;
  const lng = obj.longitude ?? obj.lng ?? obj.lon;
  const nLat = typeof lat === 'string' ? parseFloat(lat) : lat;
  const nLng = typeof lng === 'string' ? parseFloat(lng) : lng;
  if (Number.isFinite(nLat) && Number.isFinite(nLng)) return { lat: nLat, lng: nLng };
  return null;
};

/**
 * Forces Leaflet to recompute its size after mount and whenever the card's
 * container resizes. Without this the map often initializes into a zero-size
 * container inside the drawer and never recovers — the map is "there" but
 * shows nothing because it was told the viewport is 0×0.
 */
const MapAutoInvalidate = () => {
  const map = useMap();
  useEffect(() => {
    // Kick right after mount (double rAF handles the drawer's slide-in).
    const raf1 = requestAnimationFrame(() =>
      requestAnimationFrame(() => map.invalidateSize())
    );
    // And once more shortly after in case tiles are still catching up.
    const t = setTimeout(() => map.invalidateSize(), 200);

    // Watch the container for size changes — the drawer resize / breakpoint
    // change / print media all resize the card.
    const container = map.getContainer();
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(container);

    return () => {
      cancelAnimationFrame(raf1);
      clearTimeout(t);
      ro.disconnect();
    };
  }, [map]);
  return null;
};

/**
 * In-drawer preview of the trailing "Aircraft Media" row that appears at
 * the end of the generated PDF: [ route map | exterior | interior ].
 *
 * Renders a **live** react-leaflet map (not a captured PNG) so the broker
 * can inspect the route while previewing. The PDF path captures the same
 * markers via captureRouteMap and embeds a base64 image.
 *
 * Layout mirrors the mobile CSS (see tripSheetPdfGeneratorOld.ts:1028):
 *   - section marginTop 16px
 *   - title 11px uppercase bold, letter-spacing 0.64
 *   - row gap 11px, three equal-width cards
 *   - card height 120px, radius 11px, background #F0F4F8
 */
const AircraftMediaPreview = ({ data }) => {
  const aircraft = data?.aircraft || {};
  const legs = data?.legs || [];

  // Aircraft image candidates: banner_image_url first, then image_urls (deduped).
  // Matches mobile's getAircraftImageUrls at utils/api.ts:57.
  const candidateUrls = useMemo(() => {
    const seen = [];
    if (aircraft.bannerImageUrl) seen.push(aircraft.bannerImageUrl);
    (aircraft.imageUrls || []).forEach((u) => {
      if (u && !seen.includes(u)) seen.push(u);
    });
    return seen.map(resolveAssetUrl);
  }, [aircraft.bannerImageUrl, aircraft.imageUrls]);

  // Pre-validate the aircraft URLs so we only render cards that will actually
  // display an image. When every URL fails, fall through to the make_model
  // bundled image as a single card — better than two empty duplicates.
  const [aircraftImageUrls, setAircraftImageUrls] = useState([]);
  const candidateKey = candidateUrls.join('|');
  const makeModel =
    aircraft?.aircraft_make_model?.make_model ||
    aircraft?.make_model ||
    aircraft?.makeModel;
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const probed = await Promise.all(
        candidateUrls.map((u) => probeImageUrl(u).then((ok) => (ok ? u : null))),
      );
      if (cancelled) return;
      const valid = probed.filter(Boolean).slice(0, 2);
      if (valid.length > 0) {
        setAircraftImageUrls(valid);
        return;
      }
      // No aircraft URL loaded — swap in up to two bundled images from the
      // make_model directory so both media cards fill instead of leaving
      // one blank or duplicating a single fallback.
      const bundled = getAircraftBundledCarouselUrls(makeModel).slice(0, 2);
      if (bundled.length === 0) {
        if (!cancelled) setAircraftImageUrls([]);
        return;
      }
      const bundledOk = (
        await Promise.all(bundled.map((u) => probeImageUrl(u).then((ok) => (ok ? u : null))))
      ).filter(Boolean);
      if (!cancelled) setAircraftImageUrls(bundledOk);
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidateKey, makeModel]);

  // Airport points collected from every leg's departure + arrival, deduped
  // by ICAO/coordinate pair.
  const airportPoints = useMemo(() => {
    const points = [];
    const seen = new Set();
    for (const leg of legs) {
      for (const key of ['departure', 'arrival']) {
        const raw = leg?.[key];
        const coord = extractCoord(raw);
        if (!coord) continue;
        const id = `${raw?.code || ''}:${coord.lat}:${coord.lng}`;
        if (seen.has(id)) continue;
        seen.add(id);
        points.push({ ...coord, icao: raw?.code });
      }
    }
    return points;
  }, [legs]);

  // If nothing to show at all (no valid images AND no coords), skip the section.
  if (aircraftImageUrls.length === 0 && airportPoints.length === 0) return null;

  const hasRoute = airportPoints.length > 0;
  const bounds = airportPoints.length > 1
    ? airportPoints.map((p) => [p.lat, p.lng])
    : null;
  const center = airportPoints.length === 1
    ? [airportPoints[0].lat, airportPoints[0].lng]
    : null;

  const titleParts = [aircraft.tailNumber || '', aircraft.name || ''].filter(Boolean);
  const title = titleParts.join(' · ');

  // PDF media card is 172pt × 120pt (aspect 1.44). At A4 preview scale
  // (1pt = 1.333px), that's 229px × 160px. Cards get their width via
  // flex:1 (lands around ~229–236px), so height must be 160px — NOT 120 —
  // to match the PDF aspect ratio.
  // Fixed 1/3 width (matches PDF's mediaCard flexBasis:32%) so a 2-card row
  // keeps its cards at the same size as a 3-card row — the missing slot
  // stays empty instead of stretching the remaining cards horizontally.
  const cardStyle = {
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 'calc((100% - 22px) / 3)',
    minWidth: 0,
    height: 160,
    borderRadius: 11,
    overflow: 'hidden',
    backgroundColor: '#F0F4F8',
    position: 'relative',
  };
  const imgStyle = { width: '100%', height: '100%', objectFit: 'cover' };
  const placeholderStyle = {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#9CA3AF',
    fontSize: 10,
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  };

  return (
    <div style={{ marginTop: 16 }}>
      {title && (
        <div
          className="font-bold uppercase"
          style={{
            fontSize: 11,
            lineHeight: '16px',
            letterSpacing: '0.64px',
            marginBottom: 11,
            color: 'rgba(255,255,255,0.7)',
          }}
        >
          {title}
        </div>
      )}
      <div style={{ display: 'flex', gap: 11 }}>
        {/* Route map card. data-tripsheet-map tags this node so
            resolvePdfData can html2canvas it directly — one map instance
            for both drawer AND PDF, no duplicate tile fetch. */}
        <div style={cardStyle} data-tripsheet-map>
          {hasRoute ? (
            <MapContainer
              key={airportPoints.map((p) => `${p.lat},${p.lng}`).join('|')}
              bounds={bounds || undefined}
              center={center || undefined}
              zoom={center ? 8 : undefined}
              boundsOptions={{ padding: [20, 20] }}
              scrollWheelZoom={false}
              dragging={false}
              doubleClickZoom={false}
              zoomControl={false}
              attributionControl={false}
              style={{ width: '100%', height: '100%' }}
            >
              {/* crossOrigin needed so the tile <img> tags have the
                  `crossorigin` attribute — without it, html2canvas can't
                  draw them (the canvas becomes tainted). */}
              <TileLayer url={CARTO_TILES} attribution={CARTO_ATTRIBUTION} crossOrigin="anonymous" />
              {airportPoints.map((p, i) => (
                <Marker key={`${p.icao}-${i}`} position={[p.lat, p.lng]} icon={airportIcon} />
              ))}
              <MapAutoInvalidate />
            </MapContainer>
          ) : (
            <div style={placeholderStyle}>Route map</div>
          )}
        </div>
        {/* Aircraft image cards — dynamic count. Slots for broken URLs
            are omitted so the row shows exactly what will render. */}
        {aircraftImageUrls.map((url, i) => (
          <div key={i} style={cardStyle}>
            <AircraftImage
              aircraft={aircraft}
              src={url}
              alt={`Aircraft ${i + 1}`}
              style={imgStyle}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default AircraftMediaPreview;
